import Model, { attr, hasMany, type AsyncHasMany } from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import type { Type } from '@warp-drive/core-types/symbols';
import type { Repository } from 'scaleflow/utils/manual-repo-archives';
import s3PresignedUrlExpired from 'scaleflow/utils/s3-presigned-url-expired';
import type DeliverableModel from './deliverable';
import type ObservationModel from './observation';
import type RecommendationModel from './recommendation';
import type SectionModel from './section';

export default class ScanModel extends Model {
  declare [Type]: 'scan';
  @attr('string') declare title: string;
  @attr declare connector: unknown;
  @attr declare repositories: Repository[] | null;
  @attr declare status: string;
  @attr declare clone_job_status: string;
  @attr declare created_at: string;
  @attr declare updated_by: string;
  @attr declare updated_at: string;
  @attr declare analysis_job_status: string;
  @attr declare product_discovery_status: string;
  @attr declare product_config: string;
  @attr('string') declare slack_channel_id: string;
  @attr declare raw_reports_url: string;
  @attr declare publish_reports_url_credentials: string;
  @attr declare published_reports_url: string;
  @attr declare reports_url_generation_status: string;
  @attr declare vdr_delivery_and_publish_status: string;
  @attr('string') declare context: string;
  @attr('string') declare context_injection_status: string;
  // AWS Bedrock model ID the analysis runs on — see app/utils/ai-models.ts
  @attr('string') declare ai_model: string;
  @attr('boolean', { defaultValue: false }) declare archived: boolean;

  /**
   * UI-only busy flags, not persisted: each is true while a save that
   * touches this specific scan is in flight. These live on the record
   * rather than on the processing controller (a singleton reused across
   * scans) or a list row component (recycled across scans by ember-table),
   * so a save started for one scan can never show as in-progress — or,
   * worse, resolve against — a different scan that later occupies the same
   * controller/row. See docs/ai-blueprints/scan-archiving.md.
   */
  @tracked isArchiving = false;
  @tracked isSavingContext = false;
  @tracked isRefreshingCredentials = false;
  @tracked isMarkingCompleted = false;
  @tracked isMarkingAborted = false;
  @tracked isSavingAiModel = false;

  @hasMany('deliverable', {
    async: true,
    inverse: 'scan',
  })
  declare deliverables: AsyncHasMany<DeliverableModel>;

  @hasMany('observation', {
    async: true,
    inverse: 'scan',
  })
  declare observations: AsyncHasMany<ObservationModel>;

  @hasMany('recommendation', {
    async: true,
    inverse: 'scan',
  })
  declare recommendations: AsyncHasMany<RecommendationModel>;

  @hasMany('section', {
    async: true,
    inverse: 'scan',
  })
  declare sections: AsyncHasMany<SectionModel>;

  get featuredDeliverables() {
    return (this.deliverables.content ?? []).filter(
      (deliverable) => deliverable.is_featured,
    );
  }

  get reportCredentialsHaveExpired() {
    return s3PresignedUrlExpired(this.raw_reports_url);
  }

  /**
   * Nothing further can be done to progress or retry a scan in one of these
   * states — the processing page hides every action control except
   * archive/un-archive once this is true.
   */
  get isTerminal() {
    const terminalStatuses = ['COMPLETED', 'FAILED', 'TIMED_OUT', 'ABORTED'];
    return this.archived || terminalStatuses.includes(this.status);
  }

  get hasRunningStep() {
    const runningStatuses = ['IN_PROGRESS', 'INITIATE'];
    const repos = this.repositories;
    // Mirrors cloneJobStatus in app/controllers/scans/scan/processing.ts — keep in sync.
    const cloneJobStatus =
      repos?.length && repos.every((r) => r.status === 'COMPLETED')
        ? 'COMPLETED'
        : (this.clone_job_status ?? 'PENDING');

    return (
      runningStatuses.includes(cloneJobStatus) ||
      runningStatuses.includes(this.product_discovery_status) ||
      runningStatuses.includes(this.context_injection_status) ||
      runningStatuses.includes(this.analysis_job_status) ||
      runningStatuses.includes(this.reports_url_generation_status) ||
      runningStatuses.includes(this.vdr_delivery_and_publish_status)
    );
  }
}
